import { createAsyncThunk } from '@reduxjs/toolkit';
import { setSelectedProjectBankFolder } from 'store/filepermissions/actions/filepermissions.actions';
import { loadProjectBankFolderContents } from './loadProjectBankFolderContents.thunk';
import { loadProjectBankFormsFolderContents } from './loadProjectBankFormsFolderContents.thunk';
import { selectBankFolderAccess } from '../selectors/project-bank-access.selectors';
import {
  selectProjectFolderFilePermissions,
  selectProjectFolderFilePermissionsArray,
} from '../selectors/project-bank.selectors';
import { canOpenBankFolder } from 'scenes/ProjectBank/utils/bankAccess';
import {
  getProjectIdFromProjectBankFormsFolderId,
  isProjectBankFormsFolderId,
} from 'scenes/ProjectBank/utils/projectBankForms';

/**
 * The loaded folder the params name: by its id, or by its path when no id is
 * given (the header's back arrow knows only the path). Undefined for an id
 * that is no folder row, such as the tilaajavastuu view's.
 */
const findNamedFolder = (state, { folderId, path }) =>
  folderId
    ? selectProjectFolderFilePermissions(state)[folderId]
    : selectProjectFolderFilePermissionsArray(state).find(
        (folder) => folder.path === path
      );

/**
 * Opens a folder of the public bank and loads its contents. Every way into a
 * folder comes here (the crumbs, the tree, the tiles, Nerobotti, the picker),
 * so this is where a folder the user may not open is refused: a gallery-only
 * user may open only the images folder and what is beneath it
 * (utils/bankAccess canOpenBankFolder), and the API refuses them the rest.
 * A refused folder dispatches nothing: the thunk's `condition` stops it, and
 * the dispatch settles as a rejection with `meta.condition` set.
 *
 * @param {{folderId?: string, path?: string, skipSetSelected?: boolean}} params
 *   skipSetSelected: load the contents without selecting the folder
 */
export const selectProjectBankFolder = createAsyncThunk(
  'projectBank/selectProjectBankFolder',
  async (params, { dispatch, getState }) => {
    let folderId = params.folderId;

    const skipSetSelected = params.skipSetSelected || false;

    if (!folderId) {
      const projectFolders =
        selectProjectFolderFilePermissionsArray(getState());

      const initialFolder = projectFolders.find(
        (folder) => folder.path === params.path
      );

      if (initialFolder) {
        folderId = initialFolder.id;
      }
    }

    // Add folderId to params if it was missing
    const tempParams = { ...params, folderId };

    // If skipSetSelected is true, do not dispatch setSelectedProjectBankFolder
    if (!skipSetSelected) {
      dispatch(setSelectedProjectBankFolder(tempParams));
    }

    if (isProjectBankFormsFolderId(folderId)) {
      const projectId = getProjectIdFromProjectBankFormsFolderId(folderId);
      const isAlreadyLoaded =
        getState().projectBank.loadedProjectBankFormsByProjectId[projectId] ===
        true;

      if (!isAlreadyLoaded && projectId) {
        await dispatch(loadProjectBankFormsFolderContents(projectId));
      }

      return;
    }

    await dispatch(loadProjectBankFolderContents(folderId));
  },
  {
    condition: (params, { getState }) => {
      const state = getState();
      return canOpenBankFolder(
        findNamedFolder(state, params || {}),
        selectBankFolderAccess(state)
      );
    },
  }
);
